import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react'
import { useAccount, useDisconnect } from 'wagmi'
import { hide, show } from '@ebay/nice-modal-react'
import { useWeb3Modal } from '@web3modal/react'
import BitcoinWalletConnectDialog from '../BitcoinConnectDialog'
import { useBtc } from '@/context/BtcContext'

export type WalletType = 'Evm' | 'Btc'

interface WalletContextProps {
  currentSelectedWallet?: WalletType
  walletType?: WalletType
  setCurrentSelectedWallet: (value?: WalletType) => void
  address?: string
  isConnected?: boolean
  disconnect: () => void
  connectBtcWallet: () => void
  connectEvmWallet: () => void
  // onBtcWalletConnect?: () => void
  // onEvmWalletConnect?: () => void
}

const WalletContext = createContext<WalletContextProps>({
  currentSelectedWallet: undefined,
  walletType: undefined,
  setCurrentSelectedWallet: undefined,
  address: undefined,
  isConnected: undefined,
  disconnect: undefined,
  connectBtcWallet: undefined,
  connectEvmWallet: undefined,
  // onBtcWalletConnect: undefined,
  // onEvmWalletConnect: undefined,
})

export const useWallet = () => {
  const context = useContext(WalletContext)

  if (context === undefined) {
    throw new Error('useWallet must be used inside WalletProvider')
  }

  return context
}

export const WalletProvider = ({ children }: { children: ReactNode }) => {
  const [currentSelectedWallet, setCurrentSelectedWallet] = useState<WalletType>()
  const [walletType, setWalletType] = useState<WalletType>()

  const { open: openWeb3Modal } = useWeb3Modal()

  const { address: evmAccount, isConnected: isEvmWalletConnected } = useAccount()
  const { disconnect: disconnectEvmWallet } = useDisconnect()
  // const { address: btcAccount, isConnected: isBtcWalletConnected, disConnect: disconnectBtcWallet } = useConnect()
  const { address: btcAccount, isConnected: isBtcWalletConnected, disconnect: disconnectBtcWallet } = useBtc()

  const connectBtcWallet = () => {
    show(BitcoinWalletConnectDialog, {
      onConnect: () => {
        setCurrentSelectedWallet('Btc')
        hide(BitcoinWalletConnectDialog)
      },
    })
  }

  const connectEvmWallet = () => {
    openWeb3Modal()
    setCurrentSelectedWallet('Evm')
  }

  useEffect(() => {
    if (isEvmWalletConnected && currentSelectedWallet === 'Evm') {
      return setWalletType('Evm')
    }
    if (isBtcWalletConnected && currentSelectedWallet === 'Btc') {
      return setWalletType('Btc')
    }
    return setWalletType(undefined)
  }, [currentSelectedWallet, isBtcWalletConnected, isEvmWalletConnected])

  const address = walletType === 'Btc' ? btcAccount : walletType === 'Evm' ? evmAccount : undefined
  const disconnect = walletType === 'Btc' ? disconnectBtcWallet : walletType === 'Evm' ? disconnectEvmWallet : undefined

  return (
    <WalletContext.Provider
      value={{
        walletType,
        currentSelectedWallet,
        setCurrentSelectedWallet,
        address,
        isConnected: !!address,
        disconnect,
        connectBtcWallet,
        connectEvmWallet,
      }}
    >
      {children}
    </WalletContext.Provider>
  )
}
